import type { ConsentCategory, ConsentState } from './types.js'

const STORAGE_PREFIX = 'saas_consent_'

function storageKey(appId: string): string {
  return `${STORAGE_PREFIX}${appId}`
}

/**
 * Read the current consent state from localStorage.
 * Returns null if no consent has been recorded or localStorage is unavailable.
 */
export function getConsent(appId: string): ConsentState | null {
  try {
    const raw = localStorage.getItem(storageKey(appId))
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (!isConsentState(parsed)) return null
    return parsed
  } catch {
    return null
  }
}

/**
 * Persist a consent state to localStorage.
 */
export function setConsent(appId: string, state: ConsentState): void {
  try {
    localStorage.setItem(storageKey(appId), JSON.stringify(state))
  } catch {
    // localStorage unavailable or full -- degrade gracefully
  }
}

/**
 * Check whether consent has been recorded for the given app.
 */
export function hasConsent(appId: string): boolean {
  return getConsent(appId) !== null
}

/**
 * Remove stored consent for the given app.
 */
export function clearConsent(appId: string): void {
  try {
    localStorage.removeItem(storageKey(appId))
  } catch {
    // degrade gracefully
  }
}

/**
 * Check whether a specific consent category is enabled.
 * Returns false if no consent has been recorded.
 */
export function hasCategory(appId: string, category: ConsentCategory): boolean {
  const state = getConsent(appId)
  if (!state) return false
  return state.categories[category] === true
}

/**
 * Determine whether a consent state has expired based on the configured expiry.
 */
export function isExpired(state: ConsentState, expiryDays: number): boolean {
  const expiryMs = expiryDays * 24 * 60 * 60 * 1000
  return Date.now() - state.timestamp > expiryMs
}

/**
 * Build a ConsentState with all categories set according to the given selections.
 * Required categories (e.g. necessary) are always forced to true.
 */
export function buildConsentState(
  selectedCategories: ConsentCategory[],
  allCategories: ConsentCategory[],
  requiredCategories: ConsentCategory[],
  version: string,
): ConsentState {
  const categories = {} as Record<ConsentCategory, boolean>
  for (const cat of allCategories) {
    if (requiredCategories.includes(cat)) {
      categories[cat] = true
    } else {
      categories[cat] = selectedCategories.includes(cat)
    }
  }
  return {
    categories,
    timestamp: Date.now(),
    version,
  }
}

// --- Internal helpers ---

function isConsentState(value: unknown): value is ConsentState {
  if (typeof value !== 'object' || value === null) return false
  const obj = value as Record<string, unknown>
  if (typeof obj.timestamp !== 'number') return false
  if (typeof obj.version !== 'string') return false
  if (typeof obj.categories !== 'object' || obj.categories === null) return false
  return true
}
